/**
 * OutageAreaSelector — pick the villages an outage covers.
 *
 * Domlec and Dowasco announce by settlement name ("Portsmouth, Glanvillia,
 * Picard"), so the primary interaction is a parish-grouped checklist the
 * operator can work through while reading the utility's post. Whole-parish
 * toggles exist because a real announcement often is a whole parish.
 *
 * The map is the secondary path, for the announcements phrased as a stretch of
 * coast ("from Portsmouth to Colihaut"): drop a pin, set a radius, and every
 * village inside it is selected. It reuses `FestivalVenuePinPicker` with its
 * `haloRadiusM` prop rather than introducing a second map interaction model —
 * and note that scroll-wheel zoom is deliberately off there, since these
 * dialogs are taller than the viewport.
 */

import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { FestivalVenuePinPicker } from '@/components/FestivalVenuePinPicker';
import { Map as MapIcon, Search, X } from 'lucide-react';

export interface IslandAreaRow {
  id: string;
  name: string;
  parish: string;
  lat: number;
  lng: number;
  radius_m: number;
}

export interface OutageAreaSelectorProps {
  areas: IslandAreaRow[];
  selected: string[];
  onChange: (ids: string[]) => void;
}

/** Metres between two coordinates. Good enough at village scale. */
function metresBetween(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number {
  const R = 6_371_000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const lat1 = (aLat * Math.PI) / 180;
  const lat2 = (bLat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

const RADIUS_CHOICES_KM = [2, 5, 10, 20];

/** Parish-grouped village multi-select with an optional map radius helper. */
export default function OutageAreaSelector({
  areas,
  selected,
  onChange,
}: OutageAreaSelectorProps) {
  const [query, setQuery] = useState('');
  const [mapOpen, setMapOpen] = useState(false);
  const [pin, setPin] = useState<{ lat: number; lng: number } | null>(null);
  const [radiusKm, setRadiusKm] = useState(5);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const grouped = useMemo<[string, IslandAreaRow[]][]>(() => {
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? areas.filter(
          (a) =>
            a.name.toLowerCase().includes(needle) ||
            a.parish.toLowerCase().includes(needle),
        )
      : areas;
    const byParish: Record<string, IslandAreaRow[]> = {};
    for (const area of matched) {
      (byParish[area.parish] ??= []).push(area);
    }
    return Object.keys(byParish)
      .sort((a, b) => a.localeCompare(b))
      .map((parish) => [parish, byParish[parish]]);
  }, [areas, query]);

  /** Union that keeps insertion order without iterating a Set. */
  const union = (a: string[], b: string[]) => {
    const seen = new Set(a);
    return [...a, ...b.filter((id) => !seen.has(id))];
  };

  const toggle = (id: string) => {
    onChange(
      selectedSet.has(id) ? selected.filter((s) => s !== id) : [...selected, id],
    );
  };

  const toggleParish = (list: IslandAreaRow[]) => {
    const ids = list.map((a) => a.id);
    const allOn = ids.every((id) => selectedSet.has(id));
    onChange(
      allOn ? selected.filter((s) => !ids.includes(s)) : union(selected, ids),
    );
  };

  const applyRadius = () => {
    if (!pin) return;
    const inside = areas
      .filter(
        (a) => metresBetween(pin.lat, pin.lng, a.lat, a.lng) <= radiusKm * 1000,
      )
      .map((a) => a.id);
    onChange(union(selected, inside));
  };

  const selectedNames = areas
    .filter((a) => selectedSet.has(a.id))
    .sort((a, b) => a.parish.localeCompare(b.parish) || a.name.localeCompare(b.name));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a village or parish"
            className="pl-9"
            aria-label="Search villages"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setMapOpen((o) => !o)}
        >
          <MapIcon className="w-4 h-4 mr-1.5" />
          {mapOpen ? 'Hide map' : 'Select on map'}
        </Button>
      </div>

      {mapOpen ? (
        <div className="rounded-lg border p-3 space-y-2">
          <p className="text-xs text-gray-500">
            For announcements phrased as a stretch of coast. Drop a pin, choose a
            radius, then add everything inside it. You can still tick or untick
            individual villages afterwards.
          </p>
          <FestivalVenuePinPicker
            lat={pin?.lat ?? null}
            lng={pin?.lng ?? null}
            themeColor="#f59e0b"
            emoji="⚡"
            label="Outage centre"
            haloRadiusM={radiusKm * 1000}
            onChange={(lat, lng) => setPin({ lat, lng })}
          />
          <div className="flex items-center gap-2 flex-wrap">
            {RADIUS_CHOICES_KM.map((km) => (
              <button
                key={km}
                type="button"
                onClick={() => setRadiusKm(km)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  radiusKm === km
                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                    : 'bg-gray-100 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-700 hover:border-gray-500'
                }`}
              >
                {km} km
              </button>
            ))}
            <Button
              type="button"
              size="sm"
              disabled={!pin}
              onClick={applyRadius}
              className="ml-auto"
            >
              Add villages within {radiusKm} km
            </Button>
          </div>
        </div>
      ) : null}

      <div className="max-h-72 overflow-y-auto rounded-lg border divide-y">
        {grouped.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">
            No village matches “{query.trim()}”.
          </p>
        ) : (
          grouped.map(([parish, list]) => {
            const allOn = list.every((a) => selectedSet.has(a.id));
            return (
              <div key={parish} className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {parish}
                  </p>
                  <button
                    type="button"
                    onClick={() => toggleParish(list)}
                    className="text-[11px] font-semibold text-emerald-600 hover:text-emerald-500"
                  >
                    {allOn ? 'Clear parish' : 'Whole parish'}
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                  {list.map((area) => (
                    <label
                      key={area.id}
                      className="flex items-center gap-2 text-sm cursor-pointer rounded px-1 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                    >
                      <Checkbox
                        checked={selectedSet.has(area.id)}
                        onCheckedChange={() => toggle(area.id)}
                        aria-label={`${area.name}, ${area.parish}`}
                      />
                      <span>{area.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="flex items-start gap-2 flex-wrap">
        {selectedNames.length === 0 ? (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Pick at least one village. An outage with no villages would read as
            “the whole island”, which is a much bigger claim than you mean.
          </p>
        ) : (
          <>
            {selectedNames.map((a) => (
              <Badge
                key={a.id}
                variant="outline"
                className="border-emerald-500/30 text-emerald-600 dark:text-emerald-400 gap-1"
              >
                {a.name}
                <button
                  type="button"
                  onClick={() => toggle(a.id)}
                  aria-label={`Remove ${a.name}`}
                  className="hover:text-red-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[11px] text-gray-500 hover:text-gray-300 underline ml-1"
            >
              Clear all
            </button>
          </>
        )}
      </div>
    </div>
  );
}
